import { call, put, select } from "redux-saga/effects";
import { AuthenticationSelectors } from "Redux/AuthenticationRedux";
import ProjectActions from "Redux/ProjectRedux";
import { refreshToken } from "Sagas/AuthSagas/workers";
import { isTokenExpired } from "Utils/Interceptor";

const camelcaseKeys = require("camelcase-keys");
const decamelizeKeys = require("decamelize-keys");

export function* updateFeedSaga(api, { resolve }) {
  const response = yield call(api.updateFeed);
  const { data } = response;
  const camelisedData = camelcaseKeys(data, { deep: true });
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.updateFeedSuccess(camelisedData));
    yield call(resolve.onSuccess);
  } else {
    yield put(ProjectActions.updateFeedFailure(camelisedData));
    yield call(resolve.onFailure, response.data);
  }
}

export function* updateDetailSaga(api, { data, resolve }) {
  const token = yield select(AuthenticationSelectors.getAccessToken);
  const response = yield call(api.updateDetail, {
    token,
    data
  });
  const camelisedData = camelcaseKeys(response.data, { deep: true });
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.updateDetailSuccess(camelisedData));
    yield call(resolve.onSuccess);
  } else {
    yield put(ProjectActions.updateDetailFailure(camelisedData));
    yield call(resolve.onFailure, response.data);
  }
}

export function* submitRatingSaga(api, { data, resolve }) {
  const token = yield select(AuthenticationSelectors.getAccessToken);
  const response = yield call(api.submitRating, {
    token,
    data
  });
  const camelisedData = camelcaseKeys(response.data, { deep: true });
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.submitRatingSuccess(camelisedData));
    yield call(resolve.onSuccess);
  } else {
    yield put(ProjectActions.submitRatingFailure(camelisedData));
    yield call(resolve.onFailure, response.data);
  }
}

export function* exploreProjectsSaga(api, { resolve }) {
  const response = yield call(api.exploreProjects);
  const { data } = response;
  const camelisedData = camelcaseKeys(data);
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.exploreProjectsSuccess(camelisedData));
    resolve.onSuccess();
  } else {
    yield put(ProjectActions.exploreProjectsFailure(response.data));
    resolve.onFailure(response.data);
  }
}

export function* projectDetailSaga(api, { data }) {
  const response = yield call(api.getProjectDetails, data);
  const camelisedData = camelcaseKeys(response.data, { deep: true });
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.projectDetailSuccess(camelisedData));
  } else {
    yield put(ProjectActions.projectDetailFailure, response.data);
  }
}

export function* projectLeaderSaga(api, { data }) {
  const response = yield call(api.getProjectLeader, data);
  const camelisedData = camelcaseKeys(response.data, { deep: true });
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.projectLeaderSuccess(camelisedData));
  } else {
    yield put(ProjectActions.projectLeaderFailure(response.data));
  }
}

export function* projectResourcesSaga(api, { data }) {
  const response = yield call(api.getProjectResources, data);
  const camelisedData = camelcaseKeys(response.data, { deep: true });
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.projectResourcesSuccess(camelisedData));
  } else {
    yield put(ProjectActions.projectResourcesFailure(response.data));
  }
}

export function* projectMembersSaga(api, { data }) {
  const response = yield call(api.getProjectMembers, data);
  const camelisedData = camelcaseKeys(response.data, { deep: true });
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.projectMembersSuccess(camelisedData));
  } else {
    yield put(ProjectActions.projectMembersFailure(response.data));
  }
}

export function* joinProjectSaga(api, { data }) {
  const token = yield select(AuthenticationSelectors.getAccessToken);
  const response = yield call(api.joinProject, { token, data });
  const camelisedData = camelcaseKeys(response.data, { deep: true });
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.joinProjectSuccess([camelisedData]));
  } else {
    if (isTokenExpired(response.data)) {
      yield call(refreshToken, ProjectActions.joinProjectRequest(data));
    } else {
      yield put(ProjectActions.joinProjectFailure(camelisedData));
    }
  }
}

export function* getJoinProjectsSaga(api, { data }) {
  const token = yield select(AuthenticationSelectors.getAccessToken);
  const response = yield call(api.getJoinProjects, token);
  const camelisedData = camelcaseKeys(response.data, { deep: true });
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.getJoinProjectsSuccess(camelisedData));
  } else {
    if (isTokenExpired(response.data)) {
      yield call(refreshToken, ProjectActions.getJoinProjectRequest(data));
    } else {
      yield put(ProjectActions.getJoinProjectsFailure(response.data));
    }
  }
}

export function* getUserProjectsSaga(api, { data, resolve }) {
  const response = yield call(api.getUserProjects, data);
  const camelisedData = camelcaseKeys(response.data, { deep: true });
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.getUserProjectsSuccess(camelisedData));
    yield call(resolve.onSuccess);
  } else {
    yield put(ProjectActions.getUserProjectsFailure(response.data));
    yield call(resolve.onFailure, response.data);
  }
}

export function* createProjectSaga(api, { data }) {
  const token = yield select(AuthenticationSelectors.getAccessToken);
  const response = yield call(api.createProject, {
    token,
    data: decamelizeKeys(data)
  });
  const camelisedData = camelcaseKeys(response.data, { deep: true });
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.createProjectSuccess(camelisedData));
  } else {
    if (isTokenExpired(response.data)) {
      yield call(refreshToken, ProjectActions.createProjectRequest(data));
    } else {
      yield put(ProjectActions.createProjectFailure(camelisedData));
    }
  }
}

export function* getResourcesSaga(api) {
  const response = yield call(api.getResources);
  const camelisedData = camelcaseKeys(response.data, { deep: true });
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.getResourcesSuccess(camelisedData));
  } else {
    yield put(ProjectActions.getResourcesFailure(response.data));
  }
}

export function* addResourcesSaga(api, { data, resolve }) {
  const token = yield select(AuthenticationSelectors.getAccessToken);
  const response = yield call(api.addResources, {
    token,
    data
  });
  const camelisedData = camelcaseKeys(response.data, { deep: true });
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.addResourcesSuccess(camelisedData));
    yield call(resolve.onSuccess);
  } else {
    if (isTokenExpired(response.data)) {
      yield call(
        refreshToken,
        ProjectActions.addResourcesRequest(data, resolve)
      );
    } else {
      yield put(ProjectActions.addResourcesFailure(response.data));
      yield call(resolve.onFailure, response.data);
    }
  }
}

export function* deleteProjectSaga(api, { data, resolve }) {
  const token = yield select(AuthenticationSelectors.getAccessToken);
  const response = yield call(api.deleteProject, {
    token,
    data
  });
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.deleteProjectSuccess(response.data));
    yield call(resolve.onSuccess);
  } else {
    if (isTokenExpired(response.data)) {
      yield call(
        refreshToken,
        ProjectActions.deleteProjectRequest(data, resolve)
      );
    } else {
      yield put(ProjectActions.deleteProjectFailure(response.data));
      yield call(resolve.onFailure, response.data);
    }
  }
}

export function* updateProjectDetailsSaga(api, { data, resolve }) {
  const token = yield select(AuthenticationSelectors.getAccessToken);
  const response = yield call(api.updateProjectDetails, {
    token,
    data
  });
  const camelisedData = camelcaseKeys(response.data, { deep: true });
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.updateProjectDetailsSuccess(camelisedData));
    yield call(resolve.onSuccess);
  } else {
    if (isTokenExpired(response.data)) {
      yield call(
        refreshToken,
        ProjectActions.updateProjectDetailsRequest(data, resolve)
      );
    } else {
      yield put(ProjectActions.updateProjectDetailsFailure(camelisedData));
      yield call(resolve.onFailure, camelisedData);
    }
  }
}

export function* getCommentsSaga(api, { data, resolve }) {
  const response = yield call(api.getComments, data);
  const camelisedData = camelcaseKeys(response.data, { deep: true });
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.getCommentsSuccess(camelisedData));
    yield call(resolve.onSuccess);
  } else {
    yield put(ProjectActions.getCommentsFailure(camelisedData));
    yield call(resolve.onFailure, response.data);
  }
}

export function* postCommentSaga(api, { data, resolve }) {
  const token = yield select(AuthenticationSelectors.getAccessToken);
  const response = yield call(api.postComment, {
    token,
    data
  });
  const camelisedData = camelcaseKeys(response.data, { deep: true });
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.postCommentSuccess(camelisedData));
    yield call(resolve.onSuccess);
  } else {
    if (isTokenExpired(response.data)) {
      yield call(
        refreshToken,
        ProjectActions.postCommentRequest(data, resolve)
      );
    } else {
      yield put(ProjectActions.postCommentFailure(camelisedData));
      yield call(resolve.onFailure, response.data);
    }
  }
}

export function* getProjectImagesSaga(api, { data, resolve }) {
  const response = yield call(api.getProjectImages, data);
  const camelisedData = camelcaseKeys(response.data);
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.getProjectImagesSuccess(camelisedData));
    yield call(resolve.onSuccess);
  } else {
    yield put(ProjectActions.getProjectImagesFailure(camelisedData));
    yield call(resolve.onFailure, camelisedData);
  }
}

export function* updateProjectIconSaga(api, { data, resolve }) {
  const token = yield select(AuthenticationSelectors.getAccessToken);
  const response = yield call(api.updateProjectIcon, { token, data });
  const camelisedData = camelcaseKeys(response.data);
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.updateProjectIconSuccess(camelisedData));
    yield call(resolve.onSuccess);
  } else {
    if (isTokenExpired(response.data)) {
      yield call(
        refreshToken,
        ProjectActions.updateProjectIconRequest(data, resolve)
      );
    } else {
      yield put(ProjectActions.updateProjectIconFailure(camelisedData));
      yield call(resolve.onFailure, camelisedData);
    }
  }
}

export function* updateProjectAvatarSaga(api, { data, resolve }) {
  const token = yield select(AuthenticationSelectors.getAccessToken);
  const response = yield call(api.updateProjectAvatar, { token, data });
  const camelisedData = camelcaseKeys(response.data);
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.updateProjectAvatarSuccess(camelisedData));
    yield call(resolve.onSuccess);
  } else {
    if (isTokenExpired(response.data)) {
      yield call(
        refreshToken,
        ProjectActions.updateProjectAvatarRequest(data, resolve)
      );
    } else {
      yield put(ProjectActions.updateProjectAvatarFailure(camelisedData));
      yield call(resolve.onFailure, camelisedData);
    }
  }
}

export function* createProjectUpdateSaga(api, { data, resolve }) {
  const token = yield select(AuthenticationSelectors.getAccessToken);
  const response = yield call(api.createProjectUpdate, { token, data });
  const camelisedData = camelcaseKeys(response.data);
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.createProjectUpdateSuccess(camelisedData));
    yield call(resolve.onSuccess);
  } else {
    if (isTokenExpired(response.data)) {
      yield call(
        refreshToken,
        ProjectActions.createProjectUpdateRequest(data, resolve)
      );
    } else {
      yield put(ProjectActions.createProjectUpdateFailure(camelisedData));
      yield call(resolve.onFailure, camelisedData);
    }
  }
}

export function* getProjectUpdatesSaga(api, { data, resolve }) {
  const token = yield select(AuthenticationSelectors.getAccessToken);
  const response = yield call(api.getProjectUpdates, { token, data });
  const camelisedData = camelcaseKeys(response.data);
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.getProjectUpdatesSuccess(camelisedData));
    yield call(resolve.onSuccess);
  } else {
    if (isTokenExpired(response.data)) {
      yield call(
        refreshToken,
        ProjectActions.getProjectUpdatesRequest(data, resolve)
      );
    } else {
      yield put(ProjectActions.getProjectUpdatesFailure(camelisedData));
      yield call(resolve.onFailure, camelisedData);
    }
  }
}

export function* deleteProjectUpdateSaga(api, { data, resolve }) {
  const token = yield select(AuthenticationSelectors.getAccessToken);
  const response = yield call(api.deleteProjectUpdate, { token, data });
  const camelisedData = camelcaseKeys(response.data);
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.deleteProjectUpdateSuccess(camelisedData));
    yield call(resolve.onSuccess);
  } else {
    if (isTokenExpired(response.data)) {
      yield call(
        refreshToken,
        ProjectActions.deleteProjectUpdateRequest(data, resolve)
      );
    } else {
      yield put(ProjectActions.deleteProjectUpdateFailure(camelisedData));
      yield call(resolve.onFailure, camelisedData);
    }
  }
}

export function* addProjectImageSaga(api, { data, resolve }) {
  const token = yield select(AuthenticationSelectors.getAccessToken);
  const response = yield call(api.addProjectImage, { token, data });
  const camelisedData = camelcaseKeys(response.data);
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.addProjectImageSuccess(camelisedData));
    yield call(resolve.onSuccess);
  } else {
    if (isTokenExpired(response.data)) {
      yield call(
        refreshToken,
        ProjectActions.addProjectImageRequest(data, resolve)
      );
    } else {
      yield put(ProjectActions.addProjectImageFailure(camelisedData));
      yield call(resolve.onFailure, camelisedData);
    }
  }
}

export function* deleteProjectImageSaga(api, { data, resolve }) {
  const token = yield select(AuthenticationSelectors.getAccessToken);
  const response = yield call(api.deleteProjectImage, { token, data });
  const camelisedData = camelcaseKeys(response.data);
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.deleteProjectImageSuccess(camelisedData));
    yield call(resolve.onSuccess);
  } else {
    if (isTokenExpired(response.data)) {
      yield call(
        refreshToken,
        ProjectActions.deleteProjectImageRequest(data, resolve)
      );
    } else {
      yield put(ProjectActions.deleteProjectImageFailure(camelisedData));
      yield call(resolve.onFailure, camelisedData);
    }
  }
}

export function* saveProjectSaga(api, { data, resolve }) {
  const token = yield select(AuthenticationSelectors.getAccessToken);
  const response = yield call(api.saveProject, { token, data });
  const camelisedData = camelcaseKeys(response.data);
  if (response.status === 200 || response.status === 201) {
    yield put(ProjectActions.saveProjectSuccess(camelisedData));
    yield call(resolve.onSuccess);
  } else {
    if (isTokenExpired(response.data)) {
      yield call(
        refreshToken,
        ProjectActions.saveProjectRequest(data, resolve)
      );
    } else {
      yield put(ProjectActions.saveProjectFailure(camelisedData));
      yield call(resolve.onFailure, camelisedData);
    }
  }
}
